import { useMemo, useState } from 'react';
import { Save } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { usePropertyEditorApi } from './api-context';
import { useUnsavedGuard } from '@/hooks/useUnsavedGuard';
import { extractErrorMessage } from '@/lib/error';
import { showToast } from '@/lib/toast';
import { cn } from '@/lib/utils';
import type {
  PcAmenityCatalogueItem,
  PcAmenitySelection,
  AmenityKind,
  AmenityLevel,
  AmenitySelectionInput,
} from '@/services/api/property-content.api';
import { fieldInputClass } from './fields';

const KIND_ORDER: AmenityKind[] = ['APARTMENT', 'COMMUNITY', 'CONFIG'];
const KIND_LABELS: Record<AmenityKind, string> = {
  APARTMENT: 'Apartment',
  COMMUNITY: 'Community',
  CONFIG: 'Configuration',
};
const LEVEL_OPTIONS: { value: AmenityLevel | ''; label: string }[] = [
  { value: '', label: '—' },
  { value: 'room', label: 'Room' },
  { value: 'building', label: 'Building' },
  { value: 'config', label: 'Config' },
];

interface AmenityState {
  selected: boolean;
  level: AmenityLevel | '';
  comment: string;
}

function buildInitial(
  catalogue: PcAmenityCatalogueItem[],
  selected: PcAmenitySelection[],
): Record<number, AmenityState> {
  const byId = new Map(selected.map((s) => [s.amenityId, s]));
  const out: Record<number, AmenityState> = {};
  for (const item of catalogue) {
    const sel = byId.get(item.id);
    out[item.id] = {
      selected: sel?.selected ?? false,
      level: sel?.level ?? '',
      comment: sel?.comment ?? '',
    };
  }
  return out;
}

export function AmenitiesTab({
  propertyId,
  catalogue,
  selected,
}: {
  propertyId: string;
  catalogue: PcAmenityCatalogueItem[];
  selected: PcAmenitySelection[];
}) {
  const initial = useMemo(() => buildInitial(catalogue, selected), [catalogue, selected]);
  const [state, setState] = useState<Record<number, AmenityState>>(initial);
  const [error, setError] = useState('');
  const { useSetAmenities } = usePropertyEditorApi();
  const mutation = useSetAmenities(propertyId);

  const isDirty = useMemo(
    () => JSON.stringify(state) !== JSON.stringify(initial),
    [state, initial],
  );
  useUnsavedGuard(isDirty);

  const grouped = useMemo(() => {
    const map: Record<AmenityKind, PcAmenityCatalogueItem[]> = {
      APARTMENT: [],
      COMMUNITY: [],
      CONFIG: [],
    };
    for (const item of catalogue) {
      (map[item.kind] ??= []).push(item);
    }
    return map;
  }, [catalogue]);

  function update(id: number, patch: Partial<AmenityState>) {
    setState((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  }

  const selectedCount = Object.values(state).filter((s) => s.selected).length;

  async function handleSave() {
    setError('');
    // Send only rows that are selected OR carry data — the backend upserts per id.
    const selections: AmenitySelectionInput[] = catalogue
      .map((item) => {
        const st = state[item.id];
        const sel: AmenitySelectionInput = {
          amenityId: item.id,
          selected: st.selected,
        };
        if (st.level) sel.level = st.level;
        if (st.comment.trim()) sel.comment = st.comment.trim();
        return sel;
      })
      .filter((s) => {
        const init = initial[s.amenityId];
        const cur = state[s.amenityId];
        return (
          init.selected !== cur.selected ||
          init.level !== cur.level ||
          init.comment !== cur.comment
        );
      });

    if (selections.length === 0) {
      showToast('Nothing to save');
      return;
    }
    try {
      await mutation.mutateAsync(selections);
      showToast('Amenities saved · live on storefront');
    } catch (err) {
      setError(extractErrorMessage(err));
      showToast('Could not save amenities', 'error');
    }
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700" role="alert">
          {error}
        </div>
      )}

      <p className="text-sm text-gray-500">
        {selectedCount} of {catalogue.length} amenities selected. Selected amenity names are
        flattened onto the storefront; comments stay internal.
      </p>

      {KIND_ORDER.map((kind) => {
        const items = grouped[kind] ?? [];
        if (items.length === 0) return null;
        return (
          <section key={kind} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-gray-500">
              {KIND_LABELS[kind]} <span className="text-gray-400">({items.length})</span>
            </h3>
            <div className="space-y-3">
              {items.map((item) => {
                const st = state[item.id];
                const checkboxId = `amenity-${item.id}`;
                return (
                  <div
                    key={item.id}
                    className={cn(
                      'rounded-lg border p-3',
                      st.selected ? 'border-brand-200 bg-brand-50/40' : 'border-gray-100',
                    )}
                  >
                    <div className="flex flex-wrap items-center gap-3">
                      <label htmlFor={checkboxId} className="flex items-center gap-2 text-sm font-medium text-gray-800 cursor-pointer min-w-[200px]">
                        <input
                          id={checkboxId}
                          type="checkbox"
                          checked={st.selected}
                          onChange={(e) => update(item.id, { selected: e.target.checked })}
                          className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                        />
                        {item.name}
                      </label>
                      {st.selected && (
                        <>
                          <label className="flex items-center gap-1.5 text-xs text-gray-500">
                            Level
                            <select
                              aria-label={`${item.name} level`}
                              value={st.level}
                              onChange={(e) => update(item.id, { level: e.target.value as AmenityLevel | '' })}
                              className={cn(fieldInputClass, 'py-1 w-auto')}
                            >
                              {LEVEL_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>
                                  {o.label}
                                </option>
                              ))}
                            </select>
                          </label>
                          <input
                            aria-label={`${item.name} comment (internal)`}
                            value={st.comment}
                            onChange={(e) => update(item.id, { comment: e.target.value })}
                            placeholder="Comment (internal)"
                            maxLength={500}
                            className={cn(fieldInputClass, 'flex-1 min-w-[180px] py-1')}
                          />
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}

      <div className="sticky bottom-0 flex items-center justify-between gap-3 border-t border-gray-200 bg-white/90 py-3 backdrop-blur">
        <span className="text-sm text-gray-500" aria-live="polite">
          {isDirty ? 'You have unsaved changes.' : 'All changes saved.'}
        </span>
        <Button onClick={handleSave} isLoading={mutation.isPending} disabled={!isDirty || mutation.isPending}>
          <Save size={16} aria-hidden="true" />
          Save amenities
        </Button>
      </div>
    </div>
  );
}
